import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useResendPortageInvitation } from '@/hooks/queries/usePortageCompanies'
import { formatPortageContactLabel } from '@/services/portageService'
import { PortageChangeContactDialog } from '@/components/portage/PortageChangeContactDialog'
import { useTeamContext } from '@/hooks/useTeamContext'
import { USER_ROLES } from '@/config/permissions'

export interface PortageCompanyBannerData {
  id: string
  name?: string | null
  email: string
  status: string
  created_at: string
}

export interface PortageAffiliationBannerData {
  id?: string
  status: string
  portage_contact_id?: string | null
  invitation_sent_at?: string | null
  contact?: {
    id?: string
    first_name?: string | null
    last_name?: string | null
    email: string
    status?: string
  } | null
}

interface PortageInvitationBannerProps {
  company: PortageCompanyBannerData | null | undefined
  affiliation?: PortageAffiliationBannerData | null | undefined
  /** Freelancer concerné (soi-même côté freelance, id de la fiche côté équipe). */
  freelancerId: string
  className?: string
}

/**
 * Faut-il afficher le bandeau d'invitation portage ? Source de vérité unique de la condition
 * (réutilisée par le bandeau lui-même, KycBanner côté freelance et la page détail freelance côté
 * company) : société ou affiliation encore en attente, et affiliation non rejetée.
 */
export function shouldShowPortageBanner(
  company: PortageCompanyBannerData | null | undefined,
  affiliation?: PortageAffiliationBannerData | null | undefined
): boolean {
  if (!company) return false
  if (affiliation?.status === 'rejected') return false
  const companyPending = company.status === 'invited'
  const affiliationPending = affiliation?.status === 'pending_confirmation'
  const contactPending = affiliation?.contact?.status === 'pending'
  return companyPending || affiliationPending || contactPending
}

/**
 * Bandeau affiché tant que la société de portage ou l'affiliation est en attente de confirmation.
 */
export function PortageInvitationBanner({
  company,
  affiliation,
  freelancerId,
  className,
}: PortageInvitationBannerProps) {
  const { t, i18n } = useTranslation()
  const { effectiveRole } = useTeamContext()
  const resendMutation = useResendPortageInvitation()
  const [changeContactOpen, setChangeContactOpen] = useState(false)

  if (!company || !shouldShowPortageBanner(company, affiliation)) {
    return null
  }

  const canChangeContact = affiliation?.status === 'pending_confirmation' && !!freelancerId
  const addContactVariant =
    effectiveRole === USER_ROLES.FREELANCER ? 'freelancer' : 'team'

  const contactEmail = affiliation?.contact?.email || company.email
  const contactLabel = affiliation?.contact
    ? formatPortageContactLabel(affiliation.contact)
    : null
  const currentContactId =
    affiliation?.portage_contact_id ?? affiliation?.contact?.id ?? null

  const invitationSentAt = affiliation?.invitation_sent_at ?? company.created_at

  const formattedDate = (() => {
    try {
      return new Intl.DateTimeFormat(i18n.language || 'fr', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(new Date(invitationSentAt))
    } catch {
      return invitationSentAt
    }
  })()

  const handleResend = () => {
    resendMutation.mutate(
      { companyId: company.id, freelancerId },
      {
        onSuccess: (data) => {
          if (data?.skipped) {
            toast.error(t('portageBanner.resendError'))
            return
          }
          toast.success(t('portageBanner.resendSuccess'))
        },
        onError: (error) => {
          const message =
            error instanceof Error && error.message
              ? error.message
              : t('portageBanner.resendError')
          toast.error(message)
        },
      }
    )
  }

  return (
    <>
      <Alert variant="warning" className={className}>
        <div className="flex w-full flex-col gap-0.5">
          <div className="flex w-full items-center justify-between gap-4">
            <span className="text-sm">
              {contactLabel
                ? t('portageBanner.messageWithContact', {
                    company: company.name?.trim() || t('portageBanner.companyFallback'),
                    contact: contactLabel,
                    email: contactEmail,
                    date: formattedDate,
                  })
                : t('portageBanner.message', {
                    company: company.name?.trim() || t('portageBanner.companyFallback'),
                    email: contactEmail,
                    date: formattedDate,
                  })}
            </span>
            <Button
              size="sm"
              onClick={handleResend}
              disabled={resendMutation.isPending}
              className="h-7 shrink-0 bg-amber-500 px-3 text-xs text-white hover:bg-amber-600"
            >
              {resendMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {t('portageBanner.resend')}
            </Button>
          </div>
          {canChangeContact && (
            <button
              type="button"
              className="w-fit cursor-pointer text-sm font-medium text-amber-900 underline underline-offset-2 hover:opacity-80"
              onClick={() => setChangeContactOpen(true)}
            >
              {t('portageBanner.changeContact')}
            </button>
          )}
        </div>
      </Alert>

      {canChangeContact && changeContactOpen && (
        <PortageChangeContactDialog
          open={changeContactOpen}
          onOpenChange={setChangeContactOpen}
          portageCompanyId={company.id}
          portageCompanyName={company.name}
          freelancerId={freelancerId}
          currentContactId={currentContactId}
          currentContactLabel={contactLabel}
          addContactVariant={addContactVariant}
        />
      )}
    </>
  )
}

export default PortageInvitationBanner
